import { useCallback, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Customer Favorites (Aug 2026) — mounted alongside membership/recognize
// under `/api/public/retail/{slug}/...` (routes/retail_favorites.py),
// NOT the `/api/retail/public/{slug}/...` base `useRetailCart`/
// `useRetailCatalog` use for cart/catalog — the two prefixes are a
// pre-existing, deliberate split in this codebase (commerce/membership-
// adjacent public routes vs. catalog/cart-adjacent public routes).
const FAVORITES_BASE = `${BACKEND_URL}/api/public/retail`;

// Customer Favorites (Aug 2026). Every call requires proof of an
// active membership, supplied fresh by the caller on every call
// (this hook never invents or persists a session of its own) as
// EITHER of two shapes:
//   - an OTP-verified proof: `{ token, channel, destinationValue }`
//     (the original design — mirrors how `enrollMembership`/
//     `recognize` already work).
//   - Final Operator-Validation Prerequisites correction (Aug 2026,
//     Product Owner final OTP decision) — a SAFE, non-OTP proof:
//     `{ safe: true, phone, email }`. Favorites access/changes for a
//     recognized active member are an explicitly accepted, limited-
//     risk walk-in convenience and must never require Rudras OTP.
// When neither is available (unrecognized customer, or a non-member),
// every action is a local no-op that surfaces the appropriate UI
// state instead of attempting a call that would only ever 401/403.
export default function useRetailFavorites({ slug, catalogId, onAuthRejected }) {
  const [favoritedIds, setFavoritedIds] = useState(() => new Set());
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const _hasProof = (proof) => !!(proof && (proof.token || (proof.safe && (proof.phone || proof.email))));

  const _proofBody = (proof) => (
    proof.token
      ? {
          catalog_id: catalogId,
          checkout_attempt_token: proof.token,
          destination_channel: proof.channel,
          destination_value: proof.destinationValue,
        }
      : {
          catalog_id: catalogId,
          phone: proof.phone || null,
          email: proof.email || null,
        }
  );

  // Merge-blocker correction (Aug 2026, round 3) — a 401/403 here means
  // the backend itself has just told us the proof is no longer usable
  // (expired, revoked, membership lapsed) — the ONE authoritative
  // signal that must immediately end the displayed member session,
  // never merely leave stale Favorites rendered until some later timer
  // catches up. `onAuthRejected` is the caller's `endMemberSession`;
  // optional so this hook still works standalone (e.g. in isolation
  // tests) without a caller wired up.
  const _isAuthRejection = (e) => [401, 403].includes(e?.response?.status);

  const loadFavorites = useCallback(async (proof) => {
    if (!slug || !catalogId || !_hasProof(proof)) return;
    try {
      const { data } = await axios.post(`${FAVORITES_BASE}/${slug}/favorites/list`, _proofBody(proof));
      const nextItems = data.items || [];
      setItems(nextItems);
      // Includes unavailable-but-still-favorited items too (e.g. an
      // out-of-stock product) — only a product with no resolvable
      // `product_id` at all (deleted, never re-imported) is excluded,
      // since it can never appear on the main catalog grid to need a
      // star state anyway.
      setFavoritedIds(new Set(nextItems.filter((i) => i.product_id).map((i) => i.product_id)));
      setLoaded(true);
    } catch (e) {
      // A failed load (e.g. membership lapsed mid-session) leaves the
      // star state as-is rather than throwing — the Favorites tab's
      // own empty/locked state handles the "not usable" case visibly.
      setLoaded(true);
      if (_isAuthRejection(e)) onAuthRejected?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, catalogId, onAuthRejected]);

  const addFavorite = useCallback(async (proof, productId) => {
    if (!slug || !catalogId) return false;
    // Correct the Failed PR #140/#142 Walk-In Workflow (Sep 2026) —
    // confirmed staging defect: a missing/invalid proof at click time
    // (whichever underlying cause) previously returned here SILENTLY —
    // no toast, no console signal, no network call at all, indistinguishable
    // from the star simply being unresponsive. This is exactly the
    // failure mode the Product Owner's report describes ("clicking the
    // stars did not add products to Favorites") with no visible error.
    // Surfacing this explicitly both fixes the silent-failure symptom
    // AND gives a genuine diagnostic signal for any future recurrence.
    if (!_hasProof(proof)) {
      toast.error("Please confirm your phone number to save favorites.");
      return false;
    }
    // Optimistic — restored on failure below.
    setFavoritedIds((prev) => new Set(prev).add(productId));
    try {
      const body = _proofBody(proof);
      body.product_id = productId;
      await axios.post(`${FAVORITES_BASE}/${slug}/favorites/add`, body);
      // Correct the Failed PR #140/#142 Walk-In Workflow (Sep 2026) —
      // confirmed pre-existing defect: adding a favorite from a
      // product's star only ever updated `favoritedIds` (the star's
      // own filled/outline state) — never `items`, the array
      // `FavoritesSection` actually renders as cards. A customer who
      // starred a product from the main menu grid then switched to
      // the Favorites tab would see it NOT there (still "No favorites
      // yet" or missing from the list) until an unrelated full reload
      // re-fetched it — easily read as "Favorites doesn't work" even
      // though the add itself succeeded server-side. `removeFavorite`
      // right below already keeps `items` in sync; this makes `add`
      // symmetric with it. Re-fetches from the server (rather than
      // synthesizing a card from just a product id, which this hook
      // has no product details for) so the Favorites tab always shows
      // the SAME authoritative list the backend would return anyway.
      await loadFavorites(proof);
      return true;
    } catch (e) {
      setFavoritedIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
      if (_isAuthRejection(e)) {
        onAuthRejected?.();
        return false;
      }
      const detail = e?.response?.data?.detail;
      const message = (detail && detail.message) || "Couldn't save this favorite. Please try again.";
      toast.error(message);
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, catalogId, onAuthRejected]);

  const removeFavorite = useCallback(async (proof, productId) => {
    if (!slug || !catalogId) return false;
    // Correct the Failed PR #140/#142 Walk-In Workflow (Sep 2026) —
    // same "never silent" fix as `addFavorite` above.
    if (!_hasProof(proof)) {
      toast.error("Please confirm your phone number to remove favorites.");
      return false;
    }
    const hadIt = favoritedIds.has(productId);
    setFavoritedIds((prev) => {
      const next = new Set(prev);
      next.delete(productId);
      return next;
    });
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
    try {
      const body = _proofBody(proof);
      body.product_id = productId;
      await axios.post(`${FAVORITES_BASE}/${slug}/favorites/remove`, body);
      return true;
    } catch (e) {
      if (hadIt) {
        setFavoritedIds((prev) => new Set(prev).add(productId));
      }
      if (_isAuthRejection(e)) {
        onAuthRejected?.();
        return false;
      }
      const detail = e?.response?.data?.detail;
      const message = (detail && detail.message) || "Couldn't remove this favorite. Please try again.";
      toast.error(message);
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, catalogId, favoritedIds, onAuthRejected]);

  const toggleFavorite = useCallback(async (proof, productId) => {
    if (favoritedIds.has(productId)) {
      return removeFavorite(proof, productId);
    }
    return addFavorite(proof, productId);
  }, [favoritedIds, addFavorite, removeFavorite]);

  const reset = useCallback(() => {
    setFavoritedIds(new Set());
    setItems([]);
    setLoaded(false);
  }, []);

  return { favoritedIds, items, loaded, loadFavorites, addFavorite, removeFavorite, toggleFavorite, reset };
}
